import { Alert, CountrySelect, Input, Select, Switch, TextArea } from '@components'
import { ContactType, CONTACT_TYPE_OPTIONS } from '@constants'
import { FeatureWrapper, useBusinessContext, useFeature, useFlag } from '@context'
import { BusinessCountry, createContact, IContact, IEInvoiceAddress, updateContact } from '@query'
import { setAPIErrors } from '@utils'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Trans, useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import {
  IYTJCompanyDetails,
  IYTJCompanySearchResult,
  fetchYtjCompanyDetails,
  formatYtjStreet,
  pickYtjAddress
} from '@query/integrations/ytj'
import { IHandelsregisterSearchResult } from '@query/integrations/handelsregister'
import BusinessSearchDropdown, {
  CompanyRegistryHit,
  CompanyRegistrySource
} from './BusinessSearchDropdown'
import BusinessIdDropdown from './BusinessIdDropdown'
import EInvoiceAddressDropdown from './EInvoiceAddressDropdown'
import {
  useDuplicateContactCount,
  useEInvoiceAddressQuery,
  useEInvoiceOperators,
  useInvoicingLanguages
} from '@root/query/hooks'
import {
  isGermanBusinessCountry,
  isValidDeBuyerReferenceFormat
} from '@pages/InvoicingPage/xrechnungDelivery'
interface Props {
  contact?: IContact
  createDefaults?: Partial<IContact>
  onClose?: () => void
  onCreate?: (data: Partial<IContact>) => void
}

const contactPatchFromYtj = (
  details: IYTJCompanyDetails,
  searchResult: IYTJCompanySearchResult | undefined,
  invoicingLanguage: string
): Partial<IContact> => {
  const name = details.trade_name?.name ?? searchResult?.name
  const businessId = details.business_id_details?.business_id ?? searchResult?.business_id
  const address = pickYtjAddress(details)
  return {
    ...(name ? { name } : {}),
    ...(businessId ? { contact_business_id: businessId } : {}),
    invoicing_street: formatYtjStreet(address),
    invoicing_postal_code: address?.postal_code ?? '',
    invoicing_city: address?.locality ?? '',
    invoicing_country: address?.country || 'FI',
    invoicing_language: invoicingLanguage
  }
}

const contactPatchFromHandelsregister = (
  company: IHandelsregisterSearchResult,
  invoicingLanguage: string
): Partial<IContact> => {
  const address = company.postal_address
  const businessId = company.handelsregisternummer || company.register_number
  return {
    ...(company.name ? { name: company.name } : {}),
    ...(businessId ? { contact_business_id: businessId } : {}),
    invoicing_street: address?.street ?? '',
    invoicing_postal_code: address?.postal_code ?? '',
    invoicing_city: address?.locality ?? '',
    invoicing_country: address?.country || 'DE',
    invoicing_language: invoicingLanguage
  }
}

export const ContactForm: React.FC<Props> = ({ contact, createDefaults, onClose, onCreate }) => {
  const isEdit = Boolean(contact?.id)
  const { slug, data: business } = useBusinessContext()
  const queryClient = useQueryClient()
  const [t] = useTranslation()

  const nextInvoicingLanguage = (fallback: string) => {
    const cfg = business?.country_config
    const allowed = cfg?.invoicing_languages
    const preferred = cfg?.default_language
    if (allowed?.length) {
      return preferred && allowed.includes(preferred) ? preferred : allowed[0]
    }
    if (preferred) {
      return preferred
    }
    return fallback
  }

  const initialValues = { ...(createDefaults || {}), ...(contact || {}) }
  const methods = useForm<Partial<IContact>>({
    defaultValues: {
      ...initialValues,
      invoicing_language: initialValues.invoicing_language || nextInvoicingLanguage('')
    }
  })

  const {
    control,
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors }
  } = methods
  const duplicateCount = useDuplicateContactCount(useWatch({ control, name: 'name' }), contact?.id)
  const { data: invoicingLanguageOptions = [], isLoading: invoicingLanguagesLoading } =
    useInvoicingLanguages()
  const currentInvoicingLanguage = useWatch({ control, name: 'invoicing_language' })
  const invoicingLanguageSelectOptions = useMemo(() => {
    if (
      currentInvoicingLanguage &&
      typeof currentInvoicingLanguage === 'string' &&
      !invoicingLanguageOptions.some(o => o.value === currentInvoicingLanguage)
    ) {
      return [
        ...invoicingLanguageOptions,
        { value: currentInvoicingLanguage, label: currentInvoicingLanguage, info: '' }
      ]
    }
    return invoicingLanguageOptions
  }, [invoicingLanguageOptions, currentInvoicingLanguage])
  const { mutateAsync } = useMutation<unknown, unknown, Partial<IContact>>({
    mutationFn: data =>
      isEdit ? updateContact({ slug, contactId: contact.id }, data) : createContact({ slug }, data),
    ...{
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [slug, 'contacts'] })
        onClose && onClose()
      },
      onError: error => {
        const { data, status } = (error as any) ?? {}
        if (status === 400) {
          setAPIErrors(data, setError)
        }
      }
    }
  })

  const [selectedType, isInvoicingEnabled] = useWatch({
    control,
    name: ['type', 'is_invoicing_enabled']
  })

  const removeUnusedFields = (
    data: Partial<IContact>,
    isInvoicingEnabled: boolean
  ): Partial<IContact> => {
    if (isInvoicingEnabled) return data

    const filtered = { ...data }
    const elementsToRemove = [
      'invoicing_email',
      'invoicing_street',
      'invoicing_postal_code',
      'invoicing_city',
      'invoicing_country',
      'invoicing_language',
      'invoicing_einvoice_address',
      'invoicing_einvoice_operator',
      'invoicing_tax_code',
      'buyer_postal_address_details_xml_override',
      'xrechnung_buyer_reference'
    ]

    elementsToRemove.forEach(field => delete filtered[field])

    return filtered
  }

  const onSubmit = handleSubmit(async data => {
    try {
      const filteredData = removeUnusedFields(data, isInvoicingEnabled)

      const { name_aliases, ...rest } = filteredData
      const cleanData = { ...rest, name_aliases: name_aliases?.filter(Boolean) }

      const promise = mutateAsync(cleanData)
      await toast.promise(promise, {
        loading: t('general.saving'),
        success: t('general.changesSaved'),
        error: t('general.error')
      })
      onCreate && onCreate(await promise)
    } catch {
      return null
    }
  })

  const OTHER_OPERATOR = '__other__'
  const { data: einvoiceOperators = [] } = useEInvoiceOperators()
  const einvoicingFeature = useFeature('einvoicing')
  const { enabled: ytjEnabled } = useFeature('ytj')
  const { enabled: handelsregisterEnabled } = useFeature('handelsregister')
  const apixXmlBuyerAddressOverrideEnabled = useFlag('apix_xml_buyer_address_override').enabled
  const isFinnishBusiness = business?.country === BusinessCountry.FI
  const isGermanBusiness = isGermanBusinessCountry(business?.country)
  const showFinnishEInvoicing = isFinnishBusiness && einvoicingFeature.enabled
  const showApixXmlBuyerAddressOverride = isFinnishBusiness && apixXmlBuyerAddressOverrideEnabled

  const [isOtherOperator, setIsOtherOperator] = useState(() => {
    const current = contact?.invoicing_einvoice_operator
    if (!current) return false
    return !einvoiceOperators.some(op => op.code === current)
  })

  useEffect(() => {
    if (einvoiceOperators.length === 0) return
    const current = contact?.invoicing_einvoice_operator
    if (!current) return
    if (einvoiceOperators.some(op => op.code === current)) {
      setIsOtherOperator(false)
    }
  }, [einvoiceOperators, contact?.invoicing_einvoice_operator])

  const contactBusinessId = useWatch({ control, name: 'contact_business_id' })
  const initialBusinessIdRef = useRef(contact?.contact_business_id)

  const { data: addressResults = [] } = useEInvoiceAddressQuery(
    contactBusinessId,
    showFinnishEInvoicing,
    results => {
      if (results.length === 1 && contactBusinessId !== initialBusinessIdRef.current) {
        applyAddressResult(results[0])
      }
    }
  )

  const getOperatorName = (code: string) =>
    einvoiceOperators.find(op => op.code === code)?.name ?? code

  const applyAddressResult = (result: IEInvoiceAddress) => {
    setValue('invoicing_einvoice_address', result.einvoice_address)
    setValue('invoicing_einvoice_operator', result.operator_code ?? '')
  }

  const requiredInvoicingInputOptions = {
    required: { value: isInvoicingEnabled, message: t('validation.required') }
  }

  const applyContactPatch = (patch: Partial<IContact>) => {
    for (const [key, value] of Object.entries(patch) as [
      keyof IContact,
      IContact[keyof IContact] | undefined
    ][]) {
      if (value !== undefined) {
        setValue(key, value)
      }
    }
  }

  const onCopy = async (searchResult: IYTJCompanySearchResult) => {
    try {
      const details = await fetchYtjCompanyDetails({ businessId: searchResult.business_id })
      applyContactPatch(contactPatchFromYtj(details, searchResult, nextInvoicingLanguage('fi')))
    } catch {
      toast.error(t('general.error'))
    }
  }

  const onSelectRegistryHit = (hit: CompanyRegistryHit) => {
    switch (hit.source) {
      case 'ytj':
        void onCopy(hit.company)
        return
      case 'handelsregister':
        applyContactPatch(contactPatchFromHandelsregister(hit.company, nextInvoicingLanguage('de')))
        return
      default: {
        const exhaustive: never = hit
        return exhaustive
      }
    }
  }

  const applyYtjDetails = (details: IYTJCompanyDetails) => {
    applyContactPatch(contactPatchFromYtj(details, undefined, nextInvoicingLanguage('fi')))
  }

  const registrySource: CompanyRegistrySource | null =
    isFinnishBusiness && ytjEnabled
      ? 'ytj'
      : isGermanBusiness && handelsregisterEnabled
      ? 'handelsregister'
      : null

  return (
    <StyledWrapper onSubmit={event => event.stopPropagation()}>
      <FormProvider {...methods}>
        <form id="contact-form" onSubmit={onSubmit}>
          <Section>
            <Select
              label={t('contacts.form.type')}
              required={true}
              errors={errors?.type}
              {...register('type', {
                required: { value: true, message: t('validation.required') }
              })}
              data-test="contact-form-type"
            >
              <option disabled selected value=""></option>
              {CONTACT_TYPE_OPTIONS.map(({ labelKey, value }) => (
                <option key={labelKey} value={value}>
                  {t(labelKey)}
                </option>
              ))}
            </Select>

            {duplicateCount > 0 && (
              <Alert
                title={t('contacts.form.duplicateContact.title')}
                description={t('contacts.form.duplicateContact.description', {
                  count: duplicateCount
                })}
                type="warning"
              />
            )}

            {selectedType === ContactType.BUSINESS ? (
              <>
                {registrySource ? (
                  <BusinessSearchDropdown
                    label={t('contacts.form.name')}
                    source={registrySource}
                    businessSlug={registrySource === 'handelsregister' ? slug : undefined}
                    onSelect={onSelectRegistryHit}
                  ></BusinessSearchDropdown>
                ) : (
                  <Input
                    label={t('contacts.form.name')}
                    required={true}
                    errors={errors?.name}
                    {...register('name', {
                      required: { value: true, message: t('validation.required') }
                    })}
                    data-test="contact-form-name"
                  />
                )}

                {isGermanBusiness && (
                  <Input
                    label={t('contacts.form.businessIdGermany')}
                    info={t('contacts.form.businessIdGermanyInfo')}
                    errors={errors?.contact_business_id}
                    {...register('contact_business_id')}
                    data-test="contact-form-business-id-germany"
                  />
                )}

                {isFinnishBusiness && ytjEnabled && (
                  <BusinessIdDropdown
                    onCopy={applyYtjDetails}
                    required={isInvoicingEnabled}
                  ></BusinessIdDropdown>
                )}
              </>
            ) : (
              <>
                <Input
                  label={t('contacts.form.name')}
                  required={true}
                  errors={errors?.name}
                  {...register('name', {
                    required: { value: true, message: t('validation.required') }
                  })}
                  data-test="contact-form-name"
                />
              </>
            )}

            <Input
              label={t('contacts.form.customerId')}
              info={t('contacts.form.customerIdInfo')}
              {...register('customer_id')}
              errors={errors?.customer_id}
              data-test="contact-form-customer-id"
            />

            <Input
              label={t('contacts.form.phoneNumber')}
              {...register('phone_number')}
              errors={errors?.phone_number}
              data-test="contact-form-phone-number"
            />

            <InvoicingContainer>
              <div className="switch-wrapper">
                <Controller
                  control={control}
                  name="is_invoicing_enabled"
                  render={({ field: { ref, value, onChange } }) => (
                    <Switch
                      id="enable-invoicing-switch"
                      ref={ref}
                      label={t('contacts.form.activateInvoicing')}
                      checked={value}
                      onChange={() => onChange(!value)}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          event.stopPropagation()
                          onChange(!value)
                        }
                      }}
                    />
                  )}
                />
              </div>

              {isInvoicingEnabled && (
                <>
                  <br />
                  <Input
                    label={t('contacts.form.email')}
                    required={true}
                    errors={errors?.invoicing_email}
                    {...register('invoicing_email', requiredInvoicingInputOptions)}
                  />
                  {selectedType === ContactType.BUSINESS && (
                    <>
                      {showFinnishEInvoicing && (
                        <FeatureWrapper feature="einvoicing">
                          <>
                            <br />
                            <EInvoiceAddressDropdown
                              addressResults={addressResults}
                              onSelect={applyAddressResult}
                              getOperatorName={getOperatorName}
                            />
                            <Controller
                              control={control}
                              name="invoicing_einvoice_operator"
                              render={({ field }) => (
                                <>
                                  <Select
                                    label={t('contacts.form.einvoiceOperator')}
                                    info={t('contacts.form.einvoiceOperatorInfo')}
                                    errors={errors?.invoicing_einvoice_operator}
                                    value={isOtherOperator ? OTHER_OPERATOR : field.value ?? ''}
                                    onChange={e => {
                                      const val = e.target.value
                                      if (val === OTHER_OPERATOR) {
                                        setIsOtherOperator(true)
                                        field.onChange('')
                                      } else {
                                        setIsOtherOperator(false)
                                        field.onChange(val)
                                      }
                                    }}
                                  >
                                    <option value="">
                                      {t('contacts.form.einvoiceOperatorPlaceholder')}
                                    </option>
                                    {einvoiceOperators.map(op => (
                                      <option key={op.code} value={op.code}>
                                        {op.name} ({op.code})
                                      </option>
                                    ))}
                                    <option value={OTHER_OPERATOR}>
                                      {t('contacts.form.einvoiceOperatorOther')}
                                    </option>
                                  </Select>
                                  {isOtherOperator && (
                                    <Input
                                      label={t('contacts.form.einvoiceOperatorCustom')}
                                      value={field.value ?? ''}
                                      onChange={e => field.onChange(e.target.value)}
                                    />
                                  )}
                                </>
                              )}
                            />
                          </>
                        </FeatureWrapper>
                      )}
                      <br />
                      <Input
                        label={t('contacts.form.taxNumber')}
                        info={t('contacts.form.taxNumberInfo')}
                        errors={errors?.invoicing_tax_code}
                        {...register('invoicing_tax_code')}
                      />
                      {isGermanBusiness && (
                        <>
                          <br />
                          <Input
                            label={t('contacts.form.buyerReferenceBt10')}
                            info={t('contacts.form.buyerReferenceInfoBt10')}
                            errors={errors?.xrechnung_buyer_reference}
                            {...register('xrechnung_buyer_reference', {
                              validate: value => {
                                if (!value?.trim() || isValidDeBuyerReferenceFormat(value)) {
                                  return true
                                }

                                return t('validation.xrechnung.buyerReferenceInvalid')
                              }
                            })}
                            data-test="contact-form-xrechnung-buyer-reference"
                          />
                        </>
                      )}
                    </>
                  )}
                  <Input
                    required={true}
                    label={t('contacts.form.invoicingStreet')}
                    errors={errors?.invoicing_street}
                    {...register('invoicing_street', requiredInvoicingInputOptions)}
                  />
                  <Input
                    required={true}
                    label={t('contacts.form.invoicingPostalCode')}
                    errors={errors?.invoicing_postal_code}
                    {...register('invoicing_postal_code', requiredInvoicingInputOptions)}
                  />
                  <Input
                    required={true}
                    label={t('contacts.form.invoicingCity')}
                    errors={errors?.invoicing_city}
                    {...register('invoicing_city', requiredInvoicingInputOptions)}
                  />
                  <Controller
                    control={control}
                    name="invoicing_country"
                    rules={requiredInvoicingInputOptions}
                    render={({ field, fieldState: { error } }) => (
                      <CountrySelect
                        required={true}
                        label={t('contacts.form.invoicingCountry')}
                        errors={error}
                        {...field}
                        value={field.value ?? ''}
                      />
                    )}
                  />
                  <Select
                    required={true}
                    disabled={invoicingLanguagesLoading}
                    label={t('contacts.form.invoicingLanguage')}
                    info={t('contacts.form.invoicingLanguageHint')}
                    errors={errors?.invoicing_language}
                    {...register('invoicing_language', requiredInvoicingInputOptions)}
                  >
                    <option disabled value=""></option>
                    {invoicingLanguageSelectOptions.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                  {showApixXmlBuyerAddressOverride && (
                    <TextArea
                      label={t('contacts.form.apixXmlBuyerAddressOverride')}
                      info={t('contacts.form.apixXmlBuyerAddressOverrideInfo')}
                      errors={errors?.buyer_postal_address_details_xml_override}
                      rows={8}
                      style={{ fontFamily: 'monospace' }}
                      {...register('buyer_postal_address_details_xml_override')}
                    />
                  )}
                </>
              )}
            </InvoicingContainer>

            <TextArea
              label={t('contacts.form.notes')}
              errors={errors?.notes}
              {...register('notes')}
            />

            <Controller
              control={control}
              name="name_aliases"
              render={({ field: { ref, value, onChange }, fieldState: { error } }) => (
                <TextArea
                  inputRef={ref}
                  label={t('contacts.form.aliases')}
                  info={
                    <Trans
                      i18nKey="contacts.form.aliasesInfo"
                      components={{ strong: <strong /> }}
                    />
                  }
                  value={value?.join('\n')}
                  error={error}
                  onChange={e => {
                    const value = e.currentTarget.value
                    onChange(value.split('\n'))
                  }}
                />
              )}
            />
          </Section>
        </form>
      </FormProvider>
    </StyledWrapper>
  )
}

const StyledWrapper = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;

  form {
    flex: 1;
    overflow: auto;
  }
`

export const Section = styled.div`
  margin-bottom: 1rem;
`

const InvoicingContainer = styled.div`
  border: 3px solid ${({ theme }) => theme.colors.neutralGray};
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  margin-top: ${({ theme }) => theme.spacing.lg}rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;

  .switch-wrapper > * {
    margin-bottom: 0;
    display: flex;
    gap: ${({ theme }) => theme.spacing.sm}rem;
    align-items: center;
  }
`
